import { useRef, useState } from "react";

// Texto do evento em cartões de deslizar, no modelo "Carrossel de Cartões" do Instagram do Entre Elas
// (make_carrossel.py): cartão claro de cantos redondos, levemente inclinado, sobre a arte do evento desfocada,
// etiqueta em Cinzel espaçado e o texto em itálico vinho. Os cartões saem sozinhos da descrição
// (parágrafos separados por linha em branco, juntados até ~LIMITE_CARD letras).
const LIMITE_CARD = 190;
const INCLINACAO = [-1.6, 1.2, -0.8, 1.6, -1.2, 0.8];

function montarCards(texto: string): string[] {
  const paragrafos = String(texto || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    // parágrafo longo demais vira vários, quebrando no fim das frases
    .flatMap((p) => {
      if (p.length <= LIMITE_CARD) return [p];
      const partes: string[] = [];
      let pedaco = "";
      for (const frase of p.match(/[^.!?]+[.!?]+["”]?\s*|[^.!?]+$/g) || [p]) {
        if (pedaco && pedaco.length + frase.length > LIMITE_CARD) {
          partes.push(pedaco.trim());
          pedaco = frase;
        } else pedaco += frase;
      }
      if (pedaco.trim()) partes.push(pedaco.trim());
      return partes;
    });
  const cards: string[] = [];
  let atual = "";
  for (const p of paragrafos) {
    if (atual && atual.length + p.length > LIMITE_CARD) {
      cards.push(atual);
      atual = p;
    } else {
      atual = atual ? `${atual}\n\n${p}` : p;
    }
  }
  if (atual) cards.push(atual);
  return cards;
}

interface Props {
  title: string;
  description: string;
  imageUrl: string;
}

export default function EventDescriptionCards({ title, description, imageUrl }: Props) {
  const cards = montarCards(description);
  const trilho = useRef<HTMLDivElement>(null);
  const [ativo, setAtivo] = useState(0);

  if (cards.length === 0) return null;

  const aoRolar = () => {
    const el = trilho.current;
    const primeiro = el?.firstElementChild as HTMLElement | null;
    if (!el || !primeiro) return;
    setAtivo(Math.min(cards.length - 1, Math.round(el.scrollLeft / (primeiro.offsetWidth + 12))));
  };

  const irPara = (i: number) => {
    const el = trilho.current;
    const card = el?.children[i] as HTMLElement | undefined;
    if (el && card) el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
  };

  return (
    <div className="mb-5 -mx-5 md:-mx-6 lg:-mx-7">
      <div
        ref={trilho}
        onScroll={aoRolar}
        // o deslizar aqui troca de cartão, não de evento
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-5 md:px-6 lg:px-7 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {cards.map((texto, i) => (
          <div
            key={i}
            className="relative snap-center shrink-0 w-[88%] sm:w-[70%] rounded-xl overflow-hidden flex p-[6%]"
          >
            {/* fundo: a própria arte do evento, desfocada e clareada em rosé */}
            <img src={imageUrl} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-125 blur-xl" />
            <div className="absolute inset-0 bg-[#F4E6E1]/55" />

            <div
              className="relative w-full min-h-[320px] rounded-2xl bg-[#FDF8F6] px-5 py-6 flex flex-col items-center text-center shadow-[0_14px_32px_rgba(70,40,40,0.22)]"
              style={{ transform: `rotate(${INCLINACAO[i % INCLINACAO.length]}deg)` }}
            >
              <span
                className="text-[11px] tracking-[0.28em] uppercase text-[#B08A86]"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                {i === 0 ? title : `${i + 1} de ${cards.length}`}
              </span>
              <p className="my-auto pt-4 font-display italic text-[17px] leading-relaxed text-[#8C4A55] whitespace-pre-line">
                {texto}
              </p>
              <span className="mt-5 font-body text-[11px] tracking-wide text-[#B08A86]">
                {i < cards.length - 1 ? "arrasta para o lado →" : "@entreelas.community_"}
              </span>
            </div>
          </div>
        ))}
      </div>

      {cards.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3">
          {cards.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Cartão ${i + 1}`}
              onClick={() => irPara(i)}
              className={`h-1.5 rounded-full transition-all ${i === ativo ? "w-5 bg-[#8C4A55]" : "w-1.5 bg-[#8C4A55]/25"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
